import { useEffect, useState } from "react";
import { Lock, LogOut, Plus, ShieldCheck, ShieldAlert, Menu, KeyRound, Settings as SettingsIcon, BadgeCheck, ChevronLeft, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { LOGOUT } from "@/constants/testIds";
import { NotificationsBell } from "@/components/NotificationsBell";

const ROLE_LABELS = { owner: "Генеральный директор", director: "Директор", accountant: "Бухгалтер", viewer: "Наблюдатель" };

function fmtDate(iso) {
  return iso ? iso.slice(0, 10).split("-").reverse().join(".") : "—";
}

export function TopHeader({ onNewDocument, onToggleMobileSidebar }) {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu");
  const [license, setLicense] = useState(null);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);
  const [certMissing] = useState(() => localStorage.getItem("cryptodoc_skip_install") === "1");
  const [installOpen, setInstallOpen] = useState(false);
  const [latestRelease, setLatestRelease] = useState(null);

  useEffect(() => {
    if (certMissing) {
      api.get("/releases/latest").then(({ data }) => setLatestRelease(data)).catch(() => setLatestRelease(null));
    }
  }, [certMissing]);

  useEffect(() => {
    if (open) {
      api.get("/auth/license").then(({ data }) => setLicense(data)).catch(() => {});
      setView("menu");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    }
  }, [open]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const submitPasswordChange = async () => {
    if (!form.current_password || !form.new_password) {
      toast.error("Заполните все поля");
      return;
    }
    if (form.new_password.length < 6) {
      toast.error("Новый пароль должен содержать не менее 6 символов");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("Новые пароли не совпадают");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password, new_password: form.new_password,
      });
      toast.success("Пароль успешно изменён");
      setView("menu");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось изменить пароль");
    } finally {
      setBusy(false);
    }
  };

  return (
    <header className="h-14 bg-slate-900 border-b border-slate-800 flex items-center px-3 sm:px-5 gap-3 sm:gap-4 sticky top-0 z-40" data-testid="top-header">
      <button
        onClick={onToggleMobileSidebar}
        data-testid="mobile-sidebar-toggle"
        className="md:hidden text-slate-300 hover:text-white shrink-0"
      >
        <Menu className="w-5 h-5" />
      </button>
      <div className="flex items-center gap-2.5 shrink-0">
        <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
          <Lock className="w-4 h-4 text-white" />
        </div>
        <span className="text-white font-bold text-base font-display hidden sm:inline">КриптоДок</span>
      </div>
      {organization && (
        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 text-slate-200 text-xs" data-testid="org-switcher">
          <span className="font-medium">{organization.name}</span>
          <span className="text-slate-500">ИНН {organization.inn}</span>
        </div>
      )}
      <div className="flex items-center gap-3 ml-auto">
        {certMissing ? (
          <button
            type="button"
            onClick={() => setInstallOpen(true)}
            className="hidden md:flex items-center gap-1.5 text-xs font-semibold text-red-400 bg-red-500/25 border border-red-500/40 px-2.5 py-1.5 rounded-lg animate-pulse hover:bg-red-500/35 transition-colors"
            data-testid="certificate-indicator-badge"
          >
            <ShieldAlert className="w-3.5 h-3.5" /> Сертификат не найден · УСТАНОВИТЬ
          </button>
        ) : (
          <div className="hidden md:flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-2.5 py-1.5 rounded-lg" data-testid="certificate-indicator-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> КЭП: {user?.certificate_id}
          </div>
        )}
        <Button size="sm" onClick={onNewDocument} className="bg-blue-600 hover:bg-blue-700 text-white hidden sm:inline-flex" data-testid="header-new-doc-button">
          <Plus className="w-4 h-4 mr-1" /> Новый документ
        </Button>
        <Button size="icon" onClick={onNewDocument} className="bg-blue-600 hover:bg-blue-700 text-white sm:hidden w-8 h-8" data-testid="header-new-doc-button-mobile">
          <Plus className="w-4 h-4" />
        </Button>
        <div className="hidden sm:block">
          <NotificationsBell />
        </div>
        <div className="flex items-center gap-2 pl-3 border-l border-slate-700">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                data-testid="user-avatar"
                className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-medium text-white hover:bg-slate-600 transition-colors"
              >
                {user?.name?.[0] || "U"}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={10} className="w-80 p-0 overflow-hidden" data-testid="user-menu-popover">
              {view === "menu" && (
                <div>
                  <div className="p-4 bg-slate-50 border-b border-slate-100">
                    <div className="font-semibold text-slate-900 text-sm" data-testid="user-menu-name">{user?.name}</div>
                    <div className="text-xs text-slate-500">{ROLE_LABELS[user?.role] || user?.role} · {user?.email}</div>
                  </div>
                  <div className="p-3 border-b border-slate-100" data-testid="user-menu-license">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-1.5">
                      <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" /> Лицензия
                    </div>
                    {license ? (
                      <div className="text-xs text-slate-500 space-y-0.5">
                        <div>Действует до: <span className="font-medium text-slate-800" data-testid="license-expires-date">{fmtDate(license.expires_at)}</span></div>
                        <div>
                          Осталось дней:{" "}
                          <span
                            className={`font-medium ${license.days_remaining <= 5 ? "text-rose-600" : "text-slate-800"}`}
                            data-testid="license-days-remaining"
                          >
                            {license.days_remaining} из {license.total_days}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400">Загрузка…</div>
                    )}
                  </div>
                  <div className="p-1.5">
                    <button
                      onClick={() => setView("password")}
                      data-testid="user-menu-change-password"
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 text-sm text-slate-700 rounded-md hover:bg-slate-50"
                    >
                      <KeyRound className="w-4 h-4 text-slate-400" /> Сменить пароль
                    </button>
                    <button
                      onClick={() => {
                        setOpen(false);
                        navigate("/settings");
                      }}
                      data-testid="user-menu-settings-link"
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 text-sm text-slate-700 rounded-md hover:bg-slate-50"
                    >
                      <SettingsIcon className="w-4 h-4 text-slate-400" /> Все настройки
                    </button>
                    <button
                      onClick={handleLogout}
                      data-testid={LOGOUT.button}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 text-sm text-rose-600 rounded-md hover:bg-rose-50"
                    >
                      <LogOut className="w-4 h-4" /> Выйти
                    </button>
                  </div>
                </div>
              )}
              {view === "password" && (
                <div className="p-4">
                  <button onClick={() => setView("menu")} data-testid="user-menu-back-button" className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-3">
                    <ChevronLeft className="w-3.5 h-3.5" /> Назад
                  </button>
                  <div className="text-sm font-semibold text-slate-800 mb-3">Смена пароля</div>
                  <div className="space-y-2.5">
                    <div>
                      <Label className="text-xs">Текущий пароль</Label>
                      <Input
                        type="password"
                        value={form.current_password}
                        onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                        data-testid="change-password-current-input"
                        className="h-8 text-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Новый пароль</Label>
                      <Input
                        type="password"
                        value={form.new_password}
                        onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                        data-testid="change-password-new-input"
                        className="h-8 text-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Повторите новый пароль</Label>
                      <Input
                        type="password"
                        value={form.confirm_password}
                        onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                        onKeyDown={(e) => e.key === "Enter" && submitPasswordChange()}
                        data-testid="change-password-confirm-input"
                        className="h-8 text-sm mt-1"
                      />
                    </div>
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={submitPasswordChange}
                      data-testid="change-password-submit-button"
                      className="w-full bg-slate-900 hover:bg-slate-800 mt-1"
                    >
                      Сохранить новый пароль
                    </Button>
                  </div>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <Dialog open={installOpen} onOpenChange={setInstallOpen}>
        <DialogContent className="sm:max-w-md" data-testid="install-cert-modal">
          <DialogHeader>
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#4E9BEE] to-[#2563EB] flex items-center justify-center mb-2">
              <ShieldAlert className="w-5 h-5 text-white" />
            </div>
            <DialogTitle>Сертификат не найден</DialogTitle>
            <DialogDescription>
              Ваша электронная подпись хранится в приложении КриптоДок, а не в браузере. Установите
              программу на это устройство, чтобы подписывать документы.
            </DialogDescription>
          </DialogHeader>
          <a
            href={latestRelease ? `${API}/releases/latest/download` : "#"}
            data-testid="install-cert-modal-btn"
            className="flex items-center justify-center gap-2 text-sm font-semibold text-white px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 active:scale-[0.98] transition-all shadow-md shadow-green-200"
          >
            <Download className="w-4 h-4" /> Установить в один клик
          </a>
        </DialogContent>
      </Dialog>
    </header>
  );
}
